import React, { useState, useEffect } from 'react';
import { X, Shield, ScrollText, Heart, Map, Database, Server, Building2, Scale } from 'lucide-react';
import { ApiService } from '../services/api';
import { EditableText } from './EditableText';

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'impressum' | 'privacy' | 'terms' | 'datasources';
  isAdmin?: boolean;
}

const DEFAULT_TEXTS = {
  impressum: `**ROTH AG**
[Straße und Hausnummer]
[PLZ und Ort]
Schweiz

**Kontakt**
E-Mail: info@streckenfliegen.app
Webseite: streckenfliegen.app

**Unternehmensinformationen**
Handelsregister: [Eintrag / Nummer]
UID-Nummer: [UID / CHE-Nummer]
Geschäftsführung: [Name]

*Haftungsausschluss: Trotz sorgfältiger inhaltlicher Kontrolle übernehmen wir keine Haftung für die Inhalte externer Links. Für den Inhalt der verlinkten Seiten sind ausschließlich deren Betreiber verantwortlich. Fliegen ist ein Risikosport. Die Nutzung der generierten KI-Routen und Fluganalysen erfolgt ausschließlich auf eigene Gefahr.*`,
  privacy: `**Datenschutzerklärung**
*Gültig ab: 04. Mai 2026. Diese Datenschutzerklärung richtet sich nach dem revidierten Schweizer Datenschutzgesetz (DSG) sowie subsidiär nach der EU-Datenschutzgrundverordnung (DSGVO).*

# 1. Einleitung und Verantwortlicher
Der Schutz deiner Privatsphäre ist für uns von höchster Bedeutung. Diese Datenschutzerklärung klärt dich über die Art, den Umfang und den Zweck der Verarbeitung von personenbezogenen Daten auf. Verantwortlicher ist die ROTH AG.

# 2. Erfassung und Zweck der Datenverarbeitung
Wir verarbeiten personenbezogene Daten nur unter Einhaltung der einschlägigen Datenschutzbestimmungen:
- **Bestandsdaten:** Name, E-Mail-Adresse für die Registrierung.
- **Zahlungsdaten:** Werden von Stripe verarbeitet. Wir speichern keine Kreditkartendaten.
- **Hochsensible Profil- und Flugdaten:** Technische Daten sowie psychologische und physische Metriken (Reaktion in Stresssituationen, Erfahrung).

**Zweckbindung:** Diese hochsensiblen Daten werden ausschließlich dazu verwendet, die künstliche Intelligenz (Google Gemini) zu befähigen, sichere Risikoanalysen zu generieren. Eine Weitergabe für Werbezwecke ist ausgeschlossen.

# 3. Weitergabe an Drittanbieter (Auftragsverarbeiter)
- **Microsoft Azure:** Hosting & Datenbanken (Schweiz/EU).
- **Google LLC:** Künstliche Intelligenz (Gemini API) zur Generierung von Analysen. Google ist an Standardvertragsklauseln gebunden.
- **ElevenLabs:** Text-to-Speech für Audio-Tutorials (keine nutzerbezogenen Profildaten).

# 4. Cookies und lokale Speicherung
Wir verwenden keine klassischen Tracking-Cookies. Wir nutzen den localStorage deines Browsers für essenzielle Daten:
- xc_session_user: Login-Token
- xc_profiles: Lokaler Cache deines Flugprofils
- xc_state_configs: App-Einstellungen

# 5. Datensicherheit
Wir setzen technische und organisatorische Sicherheitsmaßnahmen ein (AES-Verschlüsselung, HTTPS), um deine Daten zu schützen.

# 6. Deine Rechte
Du hast das Recht auf Auskunft, Berichtigung, Löschung und Widerruf. Bitte richte Anfragen an die im Impressum angegebene E-Mail-Adresse.`,
  terms: `**Allgemeine Geschäftsbedingungen**

# 1. Geltungsbereich
Diese AGB gelten für alle Nutzer der Plattform Streckenfliegen.app (betrieben durch ROTH AG). Mit der Nutzung akzeptiert der Nutzer diese AGB.

# 2. Leistungsgegenstand
Die Plattform bietet Tools zur Streckenflugplanung, Risikoanalyse und KI-gestützte Beratung. Alle durch die KI generierten Routen und Analysen sind als Empfehlungen zu verstehen und ersetzen keinesfalls die eigene Flugvorbereitung.

# 3. Haftungsausschluss (WICHTIG)
**Gleitschirmfliegen ist eine Risikosportart, die ein hohes Maß an Eigenverantwortung erfordert.**
Der Betreiber haftet nicht für Unfälle, Personen- oder Sachschäden, die aus der Nutzung der Plattform resultieren. Die Einschätzung der Wetterlage, der Lufträume (inkl. NOTAMs) und der eigenen Fähigkeiten liegt allein beim Piloten.

# 4. Preise und Abonnements
Bestimmte Funktionen sind kostenpflichtig. Abonnements verlängern sich automatisch, sofern sie nicht vor Ablauf gekündigt werden.

# 5. Nutzungsrechte und Urheberrecht
Der Nutzer erhält ein nicht exklusives Recht, die Plattform privat zu nutzen. Die massenhafte Extraktion (Scraping) von Daten oder Karten ist untersagt.`,
  datasources: `**Datenquellen & Lizenzen**
Streckenfliegen.app baut auf herausragenden Open-Source-Projekten und offiziellen Datenquellen auf. Wir bedanken uns bei:

# Swisstopo
Hochauflösende Landeskarten und Luftbilder der Schweiz. Die beste Karte für Alpinisten.
© swisstopo (geo.admin.ch)

# Thermik & Skyways (KK7)
Das Lebenswerk von Michael von Känel. Essenzielle Thermik-Hotspots basierend auf Millionen von IGC-Tracks.
© KK7 (thermal.kk7.ch)

# SHV (Schweizerischer Hängegleiter-Verband)
Offizielle Fluggebiete, Start- und Landeplätze der Schweiz.
© SHV / FSVL (shv-fsvl.ch)

# OpenStreetMap & OpenTopoMap
Basiskarten und weltweite Topografie außerhalb der Schweiz.
© OpenStreetMap contributors, SRTM | Map style: © OpenTopoMap (CC-BY-SA)

# Leaflet.js
Die beste Open-Source JavaScript-Bibliothek für interaktive Karten.
© 2010-2023, Volodymyr Agafonkin`
};

export const LegalModal: React.FC<LegalModalProps> = ({ isOpen, onClose, initialTab = 'privacy', isAdmin = false }) => {
  const [activeTab, setActiveTab] = useState<'impressum' | 'privacy' | 'terms' | 'datasources'>(initialTab);
  const [texts, setTexts] = useState(DEFAULT_TEXTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && initialTab) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  useEffect(() => {
    if (isOpen) {
      const loadTexts = async () => {
        try {
          const settings = await ApiService.getSystemSettings();
          if (settings && settings['legal_texts']) {
            const parsed = JSON.parse(settings['legal_texts']);
            if (parsed.datasources && !parsed.datasources.includes('SHV')) {
                parsed.datasources = parsed.datasources.replace(
                    '# OpenStreetMap',
                    '# SHV (Schweizerischer Hängegleiter-Verband)\nOffizielle Fluggebiete, Start- und Landeplätze der Schweiz.\n© SHV / FSVL (shv-fsvl.ch)\n\n# OpenStreetMap'
                );
            }
            setTexts({ ...DEFAULT_TEXTS, ...parsed });
          }
        } catch (e) {
          console.error("Failed to load legal texts", e);
        }
        setLoading(false);
      };
      loadTexts();
    }
  }, [isOpen]);

  const handleSave = async (tab: keyof typeof DEFAULT_TEXTS, value: string) => {
    const newTexts = { ...texts, [tab]: value };
    setTexts(newTexts);
    try {
      await ApiService.saveSystemSetting('legal_texts', JSON.stringify(newTexts));
    } catch (e) {
      console.error("Failed to save legal texts", e);
    }
  };

  const renderMarkdown = (text: string) => {
    if (!text) return null;
    const lines = text.split('\n');
    const nodes: React.ReactNode[] = [];
    
    let currentList: React.ReactNode[] = [];
    let keyCounter = 0;

    const flushList = () => {
      if (currentList.length > 0) {
        nodes.push(<ul key={`ul-${keyCounter++}`} className="list-disc pl-5 my-2 space-y-1 text-slate-400">{currentList}</ul>);
        currentList = [];
      }
    };

    lines.forEach((line, i) => {
      // Handle Headings
      if (line.startsWith('# ')) {
        flushList();
        nodes.push(<h4 key={`h-${keyCounter++}`} className="text-lg font-bold text-white mt-6 mb-3">{line.substring(2)}</h4>);
        return;
      }
      
      // Handle Lists
      if (line.startsWith('- ')) {
        const itemContent = line.substring(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\*(.*?)\*/g, '<em>$1</em>');
        currentList.push(<li key={`li-${keyCounter++}`} dangerouslySetInnerHTML={{ __html: itemContent }} />);
        return;
      }
      
      flushList();

      // Handle empty lines (paragraphs)
      if (line.trim() === '') {
        nodes.push(<div key={`br-${keyCounter++}`} className="h-4" />);
        return;
      }

      // Handle bold and italic inline
      let parsedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>');
      parsedLine = parsedLine.replace(/\*(.*?)\*/g, '<em class="text-slate-500">$1</em>');
      
      nodes.push(<p key={`p-${keyCounter++}`} dangerouslySetInnerHTML={{ __html: parsedLine }} className="text-slate-300" />);
    });
    
    flushList();
    return nodes;
  };

  if (!isOpen) return null;

  const TabButton = ({ id, icon: Icon, label }: { id: typeof activeTab, icon: any, label: string }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-bold uppercase tracking-widest transition-all border-b-2 ${
        activeTab === id 
          ? 'border-sky-500 text-sky-400 bg-sky-500/10' 
          : 'border-transparent text-slate-400 hover:text-slate-200 hover:bg-white/5'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-slate-900 border border-slate-700/50 rounded-2xl w-full max-w-4xl h-[90vh] shadow-[0_20px_60px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="p-6 border-b border-white/5 flex justify-between items-center bg-slate-950/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400">
              <Scale className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-black text-white uppercase tracking-tight">Rechtliches & Transparenz</h2>
              <p className="text-xs text-slate-400 uppercase tracking-widest">DSG / DSGVO konforme Bestimmungen</p>
            </div>
            {isAdmin && (
              <span className="ml-4 bg-amber-500/20 text-amber-500 text-[10px] px-2 py-1 rounded-full font-bold uppercase tracking-widest border border-amber-500/20">
                Admin Edit Mode
              </span>
            )}
          </div>
          <button 
            onClick={onClose}
            title="Schließen"
            className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex overflow-x-auto custom-scrollbar bg-slate-950/30 border-b border-white/5">
          <TabButton id="impressum" icon={Building2} label="Impressum" />
          <TabButton id="privacy" icon={Shield} label="Datenschutz" />
          <TabButton id="terms" icon={ScrollText} label="AGB" />
          <TabButton id="datasources" icon={Database} label="Datenquellen" />
        </div>

        {/* Content Area */}
        <div className="p-6 md:p-8 overflow-y-auto flex-1 custom-scrollbar bg-slate-900/50 relative">
          {loading ? (
            <div className="flex items-center justify-center h-full text-slate-500">Lade Inhalte...</div>
          ) : (
            <div className="max-w-3xl text-sm leading-relaxed pb-20">
              <EditableText 
                value={texts[activeTab]} 
                onSave={(val) => handleSave(activeTab, val)} 
                mode={isAdmin ? 'edit' : 'view'} 
                multiline 
                renderView={renderMarkdown}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/5 bg-slate-950/80 shrink-0 text-center">
          <button 
            onClick={onClose}
            className="px-8 py-3 bg-sky-600 hover:bg-sky-500 text-white rounded-xl text-sm font-bold tracking-widest uppercase transition-all shadow-[0_0_15px_rgba(2,132,199,0.3)] hover:shadow-[0_0_25px_rgba(2,132,199,0.5)] active:scale-95"
          >
            Verstanden & Schließen
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalModal;
